<template>
  <NavBar pageName="Todos"></NavBar>
  <v-snackbar
      v-model="snackbarVisible"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
  >
    {{ snackbarMessage }}
    <template v-slot:action>
      <v-btn text color="red" @click="snackbarVisible = false">Close</v-btn>
    </template>
  </v-snackbar>

  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Settings
    </v-btn>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t.todos?.title || 'Task Management (Todos)' }}</span>
        <div class="d-flex ga-2">
          <v-btn
              color="primary"
              size="small"
              @click="openCreateDialog"
          >
            <v-icon class="mr-1">mdi-plus</v-icon>
            {{ t.common?.add || 'Add' }}
          </v-btn>
          <v-btn
              color="secondary"
              size="small"
              variant="outlined"
              @click="goToSettings"
          >
            {{ t.common?.settings || 'Settings' }}
          </v-btn>
        </div>
      </v-card-title>

      <!-- Filters -->
      <v-card-text class="pb-0">
        <v-row dense>
          <v-col cols="12" md="3">
            <v-select
                v-model="viewFilter"
                :items="viewOptions"
                item-value="value"
                item-title="text"
                :label="t.todos?.view || 'View'"
                density="compact"
                hide-details
                variant="outlined"
                @update:model-value="loadTodos"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-value="value"
                item-title="text"
                :label="t.common?.status || 'Status'"
                density="compact"
                hide-details
                variant="outlined"
                clearable
                @update:model-value="loadTodos"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
                v-model="filterResponsibleClientId"
                :items="clientsForFilter"
                item-value="id"
                item-title="display_name"
                :label="t.todos?.responsible_client || 'Responsible Client'"
                density="compact"
                hide-details
                variant="outlined"
                clearable
                @update:model-value="loadTodos"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
                v-model="filterResponsibleUserId"
                :items="responsibleUsers"
                item-value="id"
                item-title="display_name"
                :label="t.todos?.responsible_user || 'Responsible User'"
                density="compact"
                hide-details
                variant="outlined"
                clearable
                @update:model-value="loadTodos"
            ></v-select>
          </v-col>
        </v-row>
        <v-row dense class="mt-2">
          <v-col cols="12" md="3">
            <v-select
                v-model="sortBy"
                :items="sortOptions"
                item-value="value"
                item-title="text"
                :label="t.common?.sort_by || 'Sort By'"
                density="compact"
                hide-details
                variant="outlined"
                @update:model-value="loadTodos"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
                v-model="sortDirection"
                :items="sortDirectionOptions"
                item-value="value"
                item-title="text"
                :label="t.common?.order || 'Order'"
                density="compact"
                hide-details
                variant="outlined"
                @update:model-value="loadTodos"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-switch
                v-model="includeSubclients"
                :label="t.common?.include_subclients || 'Include Subclients'"
                density="compact"
                hide-details
                color="primary"
                @update:model-value="loadTodos"
            ></v-switch>
          </v-col>
          <v-col cols="12" md="3" class="d-flex align-center">
            <v-btn color="primary" @click="loadTodos" :loading="loading" size="small">
              <v-icon class="mr-1">mdi-refresh</v-icon>
              {{ t.common?.refresh || 'Refresh' }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-card-text>
        <v-data-table
            :headers="headers"
            :items="todos"
            item-value="id"
            class="elevation-1"
            :items-per-page="50"
            :loading="loading"
        >
          <template v-slot:[`item.status_name`]="{ item }">
            <v-chip :color="getStatusColor(item.status_name)" size="small">
              {{ item.status_name }}
            </v-chip>
          </template>

          <template v-slot:[`item.urgency`]="{ item }">
            <v-rating
                :model-value="item.urgency"
                length="3"
                readonly
                density="compact"
                size="small"
                color="warning"
            ></v-rating>
          </template>

          <template v-slot:[`item.importance`]="{ item }">
            <v-rating
                :model-value="item.importance"
                length="3"
                readonly
                density="compact"
                size="small"
                color="error"
            ></v-rating>
          </template>

          <template v-slot:[`item.completed_percentage`]="{ item }">
            <v-progress-linear
                :model-value="item.completed_percentage"
                color="primary"
                height="20"
                striped
            >
              <template v-slot:default>
                {{ item.completed_percentage }}%
              </template>
            </v-progress-linear>
          </template>

          <template v-slot:[`item.direction`]="{ item }">
            <v-chip
                :color="item.initiator_client_id === currentClientId ? 'info' : 'success'"
                size="small"
            >
              {{ item.initiator_client_id === currentClientId ? (t.todos?.sent || 'Sent') : (t.todos?.received || 'Received') }}
            </v-chip>
          </template>

          <template v-slot:[`item.responsible_user_name`]="{ item }">
            {{ item.responsible_user_name || '-' }}
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
                icon
                size="small"
                variant="text"
                color="primary"
                @click="openTodoDetails(item)"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
                icon
                size="small"
                variant="text"
                color="warning"
                @click="openEditDialog(item)"
                :disabled="item.status_name === 'completed' || item.status_name === 'cancelled'"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
                icon
                size="small"
                variant="text"
                color="error"
                @click="confirmDelete(item)"
                :disabled="item.status_name === 'completed'"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <v-btn
                v-if="item.status_name === 'pending' && item.responsible_client_id === currentClientId"
                size="x-small"
                color="warning"
                variant="tonal"
                @click="startTodo(item)"
            >
              {{ t.todos?.start || 'Start' }}
            </v-btn>
            <v-btn
                v-if="item.status_name === 'in_progress' && item.responsible_client_id === currentClientId"
                size="x-small"
                color="success"
                variant="tonal"
                @click="completeTodo(item)"
            >
              {{ t.todos?.complete || 'Complete' }}
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create/Edit Todo Dialog -->
    <v-dialog v-model="showFormDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title>
          {{ editingTodo ? (t.common?.edit || 'Edit') : (t.common?.add || 'Add') }} {{ t.todos?.todo || 'Todo' }}
        </v-card-title>
        <v-card-text>
          <v-text-field
              v-model="formData.name"
              :label="t.common?.name || 'Name'"
              variant="outlined"
              density="compact"
              :rules="[v => !!v || 'Name is required']"
              class="mb-2"
          ></v-text-field>

          <v-textarea
              v-model="formData.description"
              :label="t.common?.description || 'Description'"
              variant="outlined"
              density="compact"
              rows="3"
              class="mb-2"
          ></v-textarea>

          <v-row>
            <v-col cols="6">
              <v-select
                  v-model="formData.responsible_client_id"
                  :items="clientsForSelect"
                  item-value="id"
                  item-title="display_name"
                  :label="t.todos?.responsible_client || 'Responsible Client'"
                  variant="outlined"
                  density="compact"
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                  v-model="formData.responsible_user_id"
                  :items="responsibleUsersForClient"
                  item-value="id"
                  item-title="display_name"
                  :label="t.todos?.responsible_user || 'Responsible User'"
                  variant="outlined"
                  density="compact"
                  clearable
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <div class="text-caption mb-1">{{ t.todos?.urgency || 'Urgency' }}</div>
              <v-rating
                  v-model="formData.urgency"
                  length="3"
                  density="compact"
                  color="warning"
                  active-color="warning"
              ></v-rating>
            </v-col>
            <v-col cols="6">
              <div class="text-caption mb-1">{{ t.todos?.importance || 'Importance' }}</div>
              <v-rating
                  v-model="formData.importance"
                  length="3"
                  density="compact"
                  color="error"
                  active-color="error"
              ></v-rating>
            </v-col>
          </v-row>

          <!-- Steps section for create -->
          <div v-if="!editingTodo" class="mt-4">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="text-subtitle-2">{{ t.todos?.steps || 'Steps' }}</span>
              <v-btn size="x-small" color="primary" variant="text" @click="addStep">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </div>
            <div v-for="(step, index) in formData.steps" :key="index" class="d-flex align-center ga-2 mb-2">
              <v-text-field
                  v-model="step.description"
                  :label="`Step ${index + 1}`"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="flex-grow-1"
              ></v-text-field>
              <v-btn icon size="x-small" color="error" variant="text" @click="removeStep(index)">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeFormDialog">{{ t.common?.cancel || 'Cancel' }}</v-btn>
          <v-btn
              color="primary"
              variant="flat"
              @click="saveTodo"
              :loading="saving"
              :disabled="!formData.name"
          >
            {{ t.common?.save || 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Todo Details Dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="700px">
      <v-card v-if="selectedTodo">
        <v-card-title>{{ selectedTodo.name }}</v-card-title>
        <v-card-subtitle>
          <v-chip :color="getStatusColor(selectedTodo.status_name)" size="small" class="mr-2">
            {{ selectedTodo.status_name }}
          </v-chip>
          <span class="text-grey">{{ t.common?.created_at || 'Created' }}: {{ formatDate(selectedTodo.created_at) }}</span>
        </v-card-subtitle>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <strong>{{ t.todos?.from || 'From' }}:</strong> {{ selectedTodo.initiator_client_name }}
            </v-col>
            <v-col cols="6">
              <strong>{{ t.todos?.to || 'To' }}:</strong> {{ selectedTodo.responsible_client_name }}
            </v-col>
          </v-row>
          <v-row v-if="selectedTodo.responsible_user_name">
            <v-col cols="12">
              <strong>{{ t.todos?.assigned_to || 'Assigned To' }}:</strong> {{ selectedTodo.responsible_user_name }}
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <div class="mb-3">
            <strong>{{ t.common?.description || 'Description' }}:</strong>
            <p class="mt-1" style="white-space: pre-wrap;">{{ selectedTodo.description }}</p>
          </div>

          <v-divider class="my-3"></v-divider>

          <div class="mb-3">
            <strong>{{ t.todos?.progress || 'Progress' }}:</strong>
            <v-progress-linear
                :model-value="selectedTodo.completed_percentage"
                color="primary"
                height="25"
                striped
                class="mt-2"
            >
              <template v-slot:default>
                {{ selectedTodo.completed_percentage }}%
              </template>
            </v-progress-linear>
          </div>

          <div v-if="selectedTodo.steps && selectedTodo.steps.length > 0">
            <strong>{{ t.todos?.steps || 'Steps' }}:</strong>
            <v-list density="compact" class="mt-2">
              <v-list-item
                  v-for="step in selectedTodo.steps"
                  :key="step.id"
              >
                <template v-slot:prepend>
                  <v-icon :color="step.completed_percentage === 100 ? 'success' : 'grey'">
                    {{ step.completed_percentage === 100 ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                  </v-icon>
                </template>
                <v-list-item-title>{{ step.description }}</v-list-item-title>
                <template v-slot:append>
                  <v-btn
                      v-if="selectedTodo.responsible_client_id === currentClientId && step.completed_percentage < 100"
                      size="x-small"
                      color="success"
                      variant="text"
                      @click="completeStep(step)"
                  >
                    {{ t.todos?.done || 'Done' }}
                  </v-btn>
                  <span v-else class="text-caption">{{ step.completed_percentage }}%</span>
                </template>
              </v-list-item>
            </v-list>
          </div>

          <!-- Update Status -->
          <div v-if="selectedTodo.responsible_client_id === currentClientId" class="mt-4">
            <v-divider class="mb-3"></v-divider>
            <strong>{{ t.todos?.update_status || 'Update Status' }}:</strong>
            <v-select
                v-model="newStatus"
                :items="todoStatuses"
                item-value="name"
                item-title="name"
                :label="t.todos?.new_status || 'New Status'"
                density="compact"
                variant="outlined"
                class="mt-2"
            ></v-select>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDetailsDialog = false">{{ t.common?.close || 'Close' }}</v-btn>
          <v-btn
              v-if="selectedTodo.responsible_client_id === currentClientId"
              color="primary"
              @click="saveStatusChange"
              :loading="savingStatus"
          >
            {{ t.common?.save || 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>{{ t.common?.confirm || 'Confirm' }}</v-card-title>
        <v-card-text>
          {{ t.todos?.delete_confirm || 'Are you sure you want to delete this todo?' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">{{ t.common?.cancel || 'Cancel' }}</v-btn>
          <v-btn color="error" variant="flat" @click="deleteTodoConfirmed" :loading="deleting">
            {{ t.common?.delete || 'Delete' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { log } from "@/composables/useLog";
import { getTodos, createTodo, updateTodo, updateStep, getTodoResponsibleUsers, deleteTodo } from "@/composables/useTodosAPI";
import { getClientTree } from "@/composables/useGetClientsAPI";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";

export default {
  name: 'TodosList',
  components: { NavBar },
  data() {
    return {
      token: '',
      currentClientId: null,
      loading: false,
      saving: false,
      deleting: false,
      todos: [],
      todoStatuses: [],
      appSections: [],
      clients: [],
      responsibleUsers: [],

      // Filters
      viewFilter: 'all',
      statusFilter: null,
      filterResponsibleClientId: null,
      filterResponsibleUserId: null,
      sortBy: 'created_at',
      sortDirection: 'desc',
      includeSubclients: true,

      viewOptions: [
        { value: 'all', text: 'All' },
        { value: 'received', text: 'Received' },
        { value: 'sent', text: 'Sent' }
      ],
      statusOptions: [
        { value: null, text: 'All' },
        { value: 'pending', text: 'Pending' },
        { value: 'in_progress', text: 'In Progress' },
        { value: 'completed', text: 'Completed' },
        { value: 'cancelled', text: 'Cancelled' }
      ],
      sortOptions: [
        { value: 'created_at', text: 'Date Created' },
        { value: 'completed_percentage', text: 'Completion %' },
        { value: 'importance', text: 'Importance' },
        { value: 'urgency', text: 'Urgency' },
        { value: 'updated_at', text: 'Last Updated' }
      ],
      sortDirectionOptions: [
        { value: 'desc', text: 'Descending' },
        { value: 'asc', text: 'Ascending' }
      ],

      // Snackbar
      snackbarVisible: false,
      snackbarMessage: "",
      snackbarColor: "success",
      snackbarTimeout: 2000,

      // Form dialog
      showFormDialog: false,
      editingTodo: null,
      formData: {
        name: '',
        description: '',
        responsible_client_id: null,
        responsible_user_id: null,
        urgency: 1,
        importance: 1,
        steps: []
      },

      // Details dialog
      showDetailsDialog: false,
      selectedTodo: null,
      newStatus: null,
      savingStatus: false,

      // Delete dialog
      showDeleteDialog: false,
      deletingTodoId: null
    };
  },
  computed: {
    t() {
      return tRef.value;
    },
    headers() {
      return [
        { title: "ID", key: "id", width: "60px" },
        { title: this.t.todos?.direction || "Direction", key: "direction", width: "100px" },
        { title: this.t.common?.name || "Name", key: "name" },
        { title: this.t.todos?.from || "From", key: "initiator_client_name", width: "150px" },
        { title: this.t.todos?.to || "To", key: "responsible_client_name", width: "150px" },
        { title: this.t.todos?.assigned_to || "Assigned", key: "responsible_user_name", width: "120px" },
        { title: this.t.todos?.urgency || "Urgency", key: "urgency", width: "100px" },
        { title: this.t.todos?.importance || "Importance", key: "importance", width: "100px" },
        { title: this.t.todos?.progress || "Progress", key: "completed_percentage", width: "130px" },
        { title: this.t.common?.status || "Status", key: "status_name", width: "100px" },
        { title: this.t.common?.date || "Date", key: "created_at", width: "100px" },
        { title: this.t.common?.actions || "Actions", key: "actions", sortable: false, width: "200px" },
      ];
    },
    clientsForFilter() {
      return this.clients.map(c => ({
        ...c,
        display_name: c.indent ? `${'—'.repeat(c.indent)} ${c.name}` : c.name
      }));
    },
    clientsForSelect() {
      // For create form - can only assign to parent or own client
      return this.clients.map(c => ({
        ...c,
        display_name: c.indent ? `${'—'.repeat(c.indent)} ${c.name}` : c.name
      }));
    },
    responsibleUsersForClient() {
      // Filter users based on selected responsible_client_id
      if (!this.formData.responsible_client_id) {
        return this.responsibleUsers.map(u => ({
          ...u,
          display_name: `${u.name} (${u.client_name})`
        }));
      }
      return this.responsibleUsers
          .filter(u => u.client_id === this.formData.responsible_client_id)
          .map(u => ({
            ...u,
            display_name: `${u.name} (${u.client_name})`
          }));
    }
  },
  watch: {
    'formData.responsible_client_id'(newVal) {
      // Clear responsible_user_id if client changes
      if (newVal && this.formData.responsible_user_id) {
        const user = this.responsibleUsers.find(u => u.id === this.formData.responsible_user_id);
        if (user && user.client_id !== newVal) {
          this.formData.responsible_user_id = null;
        }
      }
    }
  },
  methods: {
    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbarVisible = true;
    },

    goToSettings() {
      this.$router.replace({ name: "settings" });
    },

    getStatusColor(status) {
      const colors = {
        'pending': 'warning',
        'in_progress': 'info',
        'completed': 'success',
        'cancelled': 'error',
        'on_hold': 'grey'
      };
      return colors[status] || 'grey';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('ro-RO');
    },

    async loadClients() {
      try {
        const response = await getClientTree(this.token, this.currentClientId);
        if (!response.error && response.clients_list) {
          // Flatten the tree structure into a list with indent levels
          this.clients = this.flattenClientTree(response.clients_list, 0);
        }
      } catch (err) {
        log("Error loading clients:", err);
      }
    },

    flattenClientTree(nodes, indent) {
      let result = [];
      for (const node of nodes) {
        result.push({
          id: node.client_id || node.id,
          name: node.name || node.title,
          indent: indent
        });
        if (node.children && node.children.length > 0) {
          result = result.concat(this.flattenClientTree(node.children, indent + 1));
        }
      }
      return result;
    },

    async loadResponsibleUsers() {
      try {
        const response = await getTodoResponsibleUsers(this.token, true);
        if (!response.error) {
          this.responsibleUsers = (response.users || []).map(u => ({
            ...u,
            display_name: `${u.name} (${u.client_name})`
          }));
        }
      } catch (err) {
        log("Error loading responsible users:", err);
      }
    },

    async loadTodos() {
      this.loading = true;
      log("Loading todos with filters");

      const options = {
        include_subclients: this.includeSubclients,
        sort_by: this.sortBy,
        sort_direction: this.sortDirection
      };

      if (this.viewFilter && this.viewFilter !== 'all') {
        options.view = this.viewFilter;
      }
      if (this.statusFilter) {
        options.status = this.statusFilter;
      }
      if (this.filterResponsibleClientId) {
        options.filter_responsible_client_id = this.filterResponsibleClientId;
      }
      if (this.filterResponsibleUserId) {
        options.responsible_user_id = this.filterResponsibleUserId;
      }

      const response = await getTodos(this.token, options);

      if (!response.error) {
        this.todos = response.todos || [];
        this.todoStatuses = response.todo_statuses || [];
        this.appSections = response.app_sections || [];
        log("Loaded " + this.todos.length + " todos");
      } else {
        this.showSnackbar(response.message || "Error loading todos", "error");
      }

      this.loading = false;
    },

    openCreateDialog() {
      this.editingTodo = null;
      this.formData = {
        name: '',
        description: '',
        responsible_client_id: null,
        responsible_user_id: null,
        urgency: 1,
        importance: 1,
        steps: []
      };
      this.showFormDialog = true;
    },

    openEditDialog(todo) {
      this.editingTodo = todo;
      this.formData = {
        name: todo.name,
        description: todo.description,
        responsible_client_id: todo.responsible_client_id,
        responsible_user_id: todo.responsible_user_id,
        urgency: todo.urgency,
        importance: todo.importance,
        steps: []
      };
      this.showFormDialog = true;
    },

    closeFormDialog() {
      this.showFormDialog = false;
      this.editingTodo = null;
    },

    addStep() {
      this.formData.steps.push({ description: '' });
    },

    removeStep(index) {
      this.formData.steps.splice(index, 1);
    },

    async saveTodo() {
      this.saving = true;

      try {
        let response;
        if (this.editingTodo) {
          // Update existing
          response = await updateTodo(this.token, this.editingTodo.id, {
            name: this.formData.name,
            description: this.formData.description,
            responsible_client_id: this.formData.responsible_client_id,
            responsible_user_id: this.formData.responsible_user_id,
            urgency: this.formData.urgency,
            importance: this.formData.importance
          });
        } else {
          // Create new
          const stepsData = this.formData.steps.filter(s => s.description.trim());
          response = await createTodo(this.token, {
            name: this.formData.name,
            description: this.formData.description,
            responsible_client_id: this.formData.responsible_client_id,
            responsible_user_id: this.formData.responsible_user_id,
            urgency: this.formData.urgency,
            importance: this.formData.importance,
            steps: stepsData
          });
        }

        if (!response.error) {
          this.showSnackbar(this.editingTodo ? "Todo updated" : "Todo created", "success");
          this.closeFormDialog();
          this.loadTodos();
        } else {
          this.showSnackbar(response.message || "Error saving todo", "error");
        }
      } catch (err) {
        log("Error saving todo:", err);
        this.showSnackbar("Error saving todo", "error");
      }

      this.saving = false;
    },

    openTodoDetails(todo) {
      this.selectedTodo = { ...todo };
      this.newStatus = todo.status_name;
      this.showDetailsDialog = true;
    },

    async startTodo(todo) {
      const response = await updateTodo(this.token, todo.id, { status: 'in_progress' });
      if (!response.error) {
        this.showSnackbar("Todo started", "success");
        this.loadTodos();
      } else {
        this.showSnackbar(response.message, "error");
      }
    },

    async completeTodo(todo) {
      const response = await updateTodo(this.token, todo.id, {
        status: 'completed',
        completed_percentage: 100
      });
      if (!response.error) {
        this.showSnackbar("Todo completed!", "success");
        this.loadTodos();
      } else {
        this.showSnackbar(response.message, "error");
      }
    },

    async completeStep(step) {
      const response = await updateStep(this.token, step.id, { completed_percentage: 100 });
      if (!response.error) {
        step.completed_percentage = 100;
        if (this.selectedTodo) {
          this.selectedTodo.completed_percentage = response.todo_completed_percentage || this.selectedTodo.completed_percentage;
        }
        this.showSnackbar("Step completed!", "success");
      } else {
        this.showSnackbar(response.message, "error");
      }
    },

    async saveStatusChange() {
      if (!this.selectedTodo || !this.newStatus) return;

      this.savingStatus = true;
      const response = await updateTodo(this.token, this.selectedTodo.id, { status: this.newStatus });

      if (!response.error) {
        this.showSnackbar("Status updated!", "success");
        this.showDetailsDialog = false;
        this.loadTodos();
      } else {
        this.showSnackbar(response.message, "error");
      }

      this.savingStatus = false;
    },

    confirmDelete(todo) {
      this.deletingTodoId = todo.id;
      this.showDeleteDialog = true;
    },

    async deleteTodoConfirmed() {
      if (!this.deletingTodoId) return;

      this.deleting = true;
      const response = await deleteTodo(this.token, this.deletingTodoId);

      if (!response.error) {
        this.showSnackbar(response.message || "Todo deleted", "success");
        this.showDeleteDialog = false;
        this.deletingTodoId = null;
        this.loadTodos();
      } else {
        this.showSnackbar(response.message || "Error deleting todo", "error");
      }

      this.deleting = false;
    }
  },
  created() {
    const { getLoggedIn, token } = useGetLoggedIn();
    getLoggedIn().then(() => {
      log("TodosList created");
      this.token = token.value;
      const currentUser = store.getters.getUser;
      this.currentClientId = currentUser?.client_id;
      log("currentClientId: " + this.currentClientId);

      // Load all data in parallel
      Promise.all([
        this.loadTodos(),
        this.loadClients(),
        this.loadResponsibleUsers()
      ]);
    });
  }
};
</script>
